import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { recipeAPI } from '../api/recipeAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'
import ConfirmationModal from '../components/ConfirmationModal'
import StockItemInfo from '../components/StockItemInfo'

function RecipeListScreen() {
    const { hasPermission } = usePermissions()
    const [recipes, setRecipes] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [showDeactivated, setShowDeactivated] = useState(false)
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [itemToDelete, setItemToDelete] = useState(null)

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('recipeColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            image: 70,
            name: 250,
            output_stock_item_name: 250,
            ingredients_count: 100,
            actions: 150
        };
    });

    useEffect(() => {
        localStorage.setItem('recipeColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchRecipes() {
            try {
                setLoading(true)
                const response = await recipeAPI.getAll(currentPage, pageSize, searchTerm, showDeactivated)
                setRecipes(response.results)
                setTotalPages(response.count)
            } finally {
                setLoading(false)
            }
        }
        fetchRecipes()
    }, [currentPage, pageSize, searchTerm, showDeactivated])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handleDeleteClick = (e, recipe) => {
        e.stopPropagation();
        setItemToDelete(recipe);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (itemToDelete) {
            try {
                await recipeAPI.delete(itemToDelete._id);
                setRecipes(recipes.filter(r => r._id !== itemToDelete._id));
            } catch (error) {
                console.error('Error deleting recipe:', error);
                alert(`Failed to delete recipe: ${error.response?.data?.message || error.message}`);
            } finally {
                setShowDeleteModal(false);
                setItemToDelete(null);
            }
        }
    };

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Recipes</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search recipes..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <Button
                        variant={showDeactivated ? "secondary" : "outline-secondary"}
                        className="me-2"
                        onClick={() => {
                            setShowDeactivated(!showDeactivated);
                            setCurrentPage(1);
                        }}
                    >
                        {showDeactivated ? "Hide Deactivated" : "Show Deactivated"}
                    </Button>
                    {hasPermission('recipes.modify') && (
                        <Button
                            onClick={() => navigate('/recipe/create')}
                            variant="success"
                        >
                            Create Recipe
                        </Button>
                    )}
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="recipes_table"
                        columns={[
                            {
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.output_stock_item_data?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.output_stock_item_data} />
                                            </Tooltip>
                                        }
                                    >
                                        <img
                                            src={row.output_stock_item_data.image}
                                            alt="Output Product"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            },
                            {
                                id: 'name',
                                header: 'Name',
                                cell: (row) => highlightText(row.name, searchTerm)
                            },
                            {
                                id: 'output_stock_item_name',
                                header: 'Output Product',
                                cell: (row) => row.output_stock_item_data?.description || '-'
                            },
                            {
                                id: 'ingredients_count',
                                header: 'Ingredients',
                                cell: (row) => row.ingredients_count
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        {hasPermission('production.create') && (
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/recipe/${row._id}/execute`)
                                                }}
                                            >
                                                Execute
                                            </Button>
                                        )}
                                        {hasPermission('recipes.modify') && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={(e) => handleDeleteClick(e, row)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={recipes}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/recipe/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the recipe "${itemToDelete?.name}"? All ingredients will also be deleted.`}
            />
        </Container>
    )
}

export default RecipeListScreen
